import { createContext, useContext, useState, useEffect, useRef } from 'react';
import { authFetch } from '../utils/auth';
import { msalInstance } from '../auth/msalConfig';

/**
 * Subscription state, mirroring the ProfileContext pattern: one fetch of
 * GET /api/subscription/status post-auth, plus an explicit refresh().
 *
 * Unlike ProfileContext, refresh() never flips state back to "loading" —
 * the confirm page polls /status every few seconds, and the app shell must
 * not remount on every poll. `fetched` means "a post-auth fetch has
 * completed (success or failure)"; App.js holds its spinner on it so a
 * gated user never sees a flash of the ungated app after login.
 *
 * On fetch failure we fail OPEN (gateEnabled false): the backend gate still
 * answers 402 on every protected call, and authFetch routes 402 to
 * /abonnement — a status hiccup must not lock the UI on its own.
 */

const EMPTY = {
  status: 'none',
  gateEnabled: false,
  currentPeriodEnd: null,
  priceOre: null,
  // Server-computed access verdict (backend lib/subscriptionAccess.js).
  // The frontend consumes it verbatim and never re-derives the paid-through
  // policy. EMPTY carries hasAccess false, but gateEnabled false keeps the
  // fail-open contract: routing only gates when the backend says the gate
  // is on.
  hasAccess: false,
  accessUntil: null,
  // Server charge fact (newest charge on the agreement is FAILED) behind the
  // "Betalingsproblem" / "Utløpt – betaling feilet" labels — consumed as-is.
  lastChargeFailed: false,
  // Staff (backend ADMIN_USER_IDS): member access without a subscription.
  // Only sent for staff; absent means false.
  staffAccess: false
};

const SubscriptionContext = createContext();
export const useSubscription = () => useContext(SubscriptionContext);

export function SubscriptionProvider({ children }) {
  const [subscription, setSubscription] = useState(EMPTY);
  const [fetched, setFetched] = useState(false);
  // Dedup concurrent refreshes: on initial mount both this provider and
  // App.js's post-login effect may ask for one in the same commit.
  const inFlight = useRef(null);

  const refresh = () => {
    if (inFlight.current) return inFlight.current;
    inFlight.current = new Promise(res => {
      authFetch('/subscription/status')
        .then(r => r.json())
        .then(json => {
          const next = {
            status: json.status || 'none',
            gateEnabled: json.gateEnabled === true,
            currentPeriodEnd: json.currentPeriodEnd || null,
            priceOre: json.priceOre ?? null,
            // Server verdict, consumed verbatim (backend always returns it
            // since PR 1 went live — the deploy-race fallback is gone).
            hasAccess: json.hasAccess === true,
            accessUntil: json.accessUntil || null,
            lastChargeFailed: json.lastChargeFailed === true,
            staffAccess: json.staffAccess === true
          };
          setSubscription(next);
          setFetched(true);
          res(next);
        })
        .catch(error => {
          console.error('Error loading subscription status:', error);
          setSubscription(EMPTY);
          setFetched(true);
          res(EMPTY);
        });
    }).finally(() => { inFlight.current = null; });
    return inFlight.current;
  };

  // Initial load — only fetch if the user is already authenticated.
  // After a fresh login, App.js triggers refresh() (same pattern as the
  // profile re-fetch there).
  useEffect(() => {
    const account = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];
    if (account) {
      refresh();
    }
    // Mount-only, like ProfileContext's initial load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <SubscriptionContext.Provider value={{ ...subscription, fetched, refresh }}>
      {children}
    </SubscriptionContext.Provider>
  );
}
